// SEC-007: user.update 권한 필드 변경 가드 — FE 분기 SSoT helper.
//
// BE rayguard-api `resolveCallerPrivilege` (PR #95 R2) 와 동일 tier 등급화 정책 미러:
//   caller.adminType === 'S' (Super Admin 필수)
//     AND tier:
//       - 'hq'      (caller.org === 'HQ')         → orgScoped + hqOnly 모두 편집 가능
//       - 'sameOrg' (caller.companyId === target) → orgScoped 만 편집 (companyId=hqOnly 차단)
//       - 'none'                                  → 권한 필드 전부 차단
//
// 등급 정의 (BE 동일 SSoT — 정책 변경 시 BE `src/libs/authz/userUpdate.js` PERMISSION_FIELDS_BY_TIER
// 와 동시 갱신 필수. IMP-136: 양쪽 `permissionFieldsContract.test.js` 가 계약을 lock — ADR-002):
//   orgScoped — 운영성 필드. 자기 조직 Super Admin 도 변경 가능
//     - permAdminAccess, adminType, dealerType, isTypeChange,
//       isCreateLicense, isLicenseDownload, isUse, isSetWarranty
//   hqOnly — cross-org 영향 큰 필드. HQ Super Admin 만 변경 가능
//     - companyId (회사 이동, RLog audit). Subsidiary Super 이 same-org target 의
//       companyId 변경으로 cross-org 우회 시도 차단 (SEC-007 R2 IDOR 방어).
//
// FE 가드는 silent skip UX 차단(disabled + 자물쇠 + alert) 목적이며 BE 가 fail-closed SSoT.
// FE 만 우회해도 BE 가 차단하여 실제 변경 불가.

import { ORG_HQ } from '../config/appConstants';

export const PERMISSION_FIELDS_BY_TIER = {
    orgScoped: [
        'permAdminAccess', 'adminType', 'dealerType',
        'isTypeChange', 'isCreateLicense', 'isLicenseDownload', 'isUse',
        // SEC-040: isSetWarranty(제조사 보증 실행 권한 플래그) — orgScoped 재사용, BE writeScope su 게이트가 최종 방어.
        'isSetWarranty',
    ],
    hqOnly: [
        'companyId',
    ],
};

const ALL_PERMISSION_FIELDS = [
    ...PERMISSION_FIELDS_BY_TIER.orgScoped,
    ...PERMISSION_FIELDS_BY_TIER.hqOnly,
];

/**
 * caller 의 권한 tier 결정.
 *
 * **사용 맥락**: `AdminUserEditInfo` 가 마운트 시 `user={}` 빈 객체로 시작하고
 * `GetUser` 비동기 응답 후 채워지는 패턴이라, `targetUser.companyId` 가 일시적으로
 * falsy 일 수 있다. 본 helper 는 fail-closed 정책으로 Subsidiary Super Admin
 * 케이스에서 권한 필드를 잠시 disabled 로 표시한다 (보안 보수적 동작;
 * UX 깜빡임은 별도 IMP 로 처리). HQ Super Admin 은 cross-org 정책으로 target
 * 검증 생략하여 깜빡임 없음.
 *
 * BE `resolveCallerPrivilege` 가 fail-closed SSoT.
 *
 * **BUG-116 (SEC-007 R3)**: '같은 organization' 안내 메시지 SSoT 정합.
 *   `accessibleCompanyIds` 인자에 caller 의 sub-tree (자신 + 자손 회사) ID 목록을
 *   넘기면 그 안에서 sameOrg 판정. BE `getChildCompanyIds` 와 동치. 미지정/빈 배열
 *   이면 기존 `companyId` 정확 동등 비교로 fallback (후방 호환).
 *
 * @param {object|null|undefined} authuser    redux 세션의 인증 사용자 (state.AuthReducer.user)
 * @param {object|null|undefined} targetUser  편집 대상 user 객체. AdminUserEditInfo 의 user prop.
 * @param {string[]|null} [accessibleCompanyIds=null] caller 의 sub-tree (자신 + 자손) 회사 ID 목록.
 *   주어지면 `targetUser.companyId` 포함 여부로 sameOrg 판정. 미지정/빈 배열이면
 *   `authuser.companyId === targetUser.companyId` 정확 동등으로 fallback.
 * @returns {'hq' | 'sameOrg' | 'none'}
 */
export const getCallerTier = (authuser, targetUser, accessibleCompanyIds = null) => {
    if (!authuser) return 'none';
    if (authuser.adminType !== 'S') return 'none';

    // HQ Super Admin: cross-org 허용. target 검증 생략(신규 생성 흐름 + UX 깜빡임 회피).
    if (authuser.companyInfo && authuser.companyInfo.org === ORG_HQ) return 'hq';

    // Subsidiary/Branch Super Admin: caller 의 sub-tree (자신 + 자손) 안에 target 이 있으면 sameOrg.
    // companyId 결측 / null / 빈 문자열 은 fail-closed.
    if (!authuser.companyId) return 'none';
    if (!targetUser || !targetUser.companyId) return 'none';

    // BUG-116: accessibleCompanyIds (sub-tree) 주어지면 includes 로 판정.
    // BE `getChildCompanyIds(caller.companyId)` 와 동치 — `state.CompanyReducer.companies`
    // 가 BE `/company` getPermList 필터로 caller sub-tree 만 포함하므로 FE 동기 동치 가능.
    if (Array.isArray(accessibleCompanyIds) && accessibleCompanyIds.length > 0) {
        return accessibleCompanyIds.includes(targetUser.companyId) ? 'sameOrg' : 'none';
    }
    // Fallback: accessibleCompanyIds 미지정/빈 배열 → 기존 정확 동등 비교 (후방 호환).
    return authuser.companyId === targetUser.companyId ? 'sameOrg' : 'none';
};

/**
 * tier 가 이미 결정된 상태에서 특정 필드의 편집 가능 여부를 결정.
 * AdminUserEditInfo 처럼 한 렌더에서 여러 권한 필드에 동일 tier 를 적용할 때
 * getCallerTier 를 필드마다 재호출하는 낭비를 막기 위해 사용.
 * 권한 필드 외 fieldName 은 무조건 true 반환 (general fields fallback).
 *
 * **BUG-116 (SEC-007 R3)**: companyId 는 sameOrg tier 에서도 편집 가능.
 *   UI 의 select2 list 옵션이 redux `CompanyReducer.companies` (BE getPermList 로 sub-tree
 *   만 필터) 로 자동 제한되며, BE `sanitizeUpdateData` 가 sub-tree 외부 값을 silent drop
 *   하여 fail-closed value 검증. hqOnly tier 시맨틱은 보존되지만 실제 차단은 BE 가 담당.
 *
 * @param {string} fieldName
 * @param {'hq' | 'sameOrg' | 'none'} tier   getCallerTier 의 반환값
 * @returns {boolean}
 */
export const isFieldEditableByTier = (fieldName, tier) => {
    if (!ALL_PERMISSION_FIELDS.includes(fieldName)) return true;
    if (tier === 'hq') return true;
    if (tier === 'sameOrg') {
        // BUG-116: orgScoped + hqOnly 모두 편집 가능. companyId 값 검증은 BE 가 SSoT.
        return PERMISSION_FIELDS_BY_TIER.orgScoped.includes(fieldName)
            || PERMISSION_FIELDS_BY_TIER.hqOnly.includes(fieldName);
    }
    return false;
};

/**
 * 특정 필드를 편집 가능한지 결정. 권한 필드는 tier 분기, 그 외 필드는 항상 true.
 *
 * 권한 필드 외 fieldName 은 무조건 true 반환 (general fields fallback — 호출처에서 caller editable 과 AND 연산 필요).
 * 여러 필드에 일괄 적용할 때는 tier 를 1회 계산 후 `isFieldEditableByTier(fieldName, tier)` 패턴 권장.
 *
 * 사용 예: `isFieldEditable('dealerType', authuser, user)` → AdminUserEditInfo
 * 의 dealerType select isEditable 분기.
 *
 * @param {string} fieldName
 * @param {object|null} authuser
 * @param {object|null} targetUser
 * @returns {boolean} 권한 필드 외 필드는 항상 true. 권한 필드는 tier 에 따라 결정.
 */
export const isFieldEditable = (fieldName, authuser, targetUser) => {
    return isFieldEditableByTier(fieldName, getCallerTier(authuser, targetUser));
};

/**
 * 후방 호환 — 기존 boolean helper. `getCallerTier(...) !== 'none'` 과 동치.
 * AdminUserEditInfo 의 자물쇠/alert 분기 일부에서 "권한 필드 중 하나라도 편집 가능?" 판단용.
 *
 * @deprecated since SEC-007 R2. production 호출처 없음 (tests-only).
 *   Phase 3 에서 제거 예정 (2026-Q3 이후). 후속 cleanup 이슈 IMP 등록.
 *   `getCallerTier` 또는 `isFieldEditableByTier` 를 직접 사용할 것.
 *
 * @param {object|null} authuser
 * @param {object|null} targetUser
 * @returns {boolean}
 */
export const isPrivilegedUserUpdateCaller = (authuser, targetUser) => {
    return getCallerTier(authuser, targetUser) !== 'none';
};
